import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Expand, Loader2, MinusCircle, Shrink } from "lucide-react";
import Card from "../../components/common/Card";
import MainHeading from "../../components/common/MainHeading";
import CommonTable, {
  type TableColumn,
} from "../../components/common/CommonTable";
import CommonFilter from "../../components/common/CommonFilter";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { ThrowError } from "../../api/callApi";
import type {
  IDayColumnInfo,
  ILaborBulkAttendanceRow,
  LaborAttendanceStatus,
} from "../../interface/laborAttendance.interface";
import LaborAttendanceLegend, {
  LABOR_STATUS_OPTIONS,
  LABOR_STATUS_STYLE_MAP,
  LABOR_STATUS_ICON_MAP,
} from "./LaborAttendanceLegend";
import type { AttendanceApiStatus } from "../../interface/attendance.model";
import { AttendanceService } from "@/services/Attendance/Attendance";
import Label from "@/components/ui/Label";

const getDayColumns = (
  startDate: string,
  endDate: string,
): IDayColumnInfo[] => {
  const days: IDayColumnInfo[] = [];
  let current = moment(startDate);
  const end = moment(endDate);
  while (current.isSameOrBefore(end, "day")) {
    days.push({
      dayStr: current.format("DD"), // display only — NOT unique across months
      dayName: current.format("ddd"),
      fullDate: current.format("YYYY-MM-DD"), // unique key — use this everywhere else
      isSunday: current.day() === 0,
    });
    current = current.clone().add(1, "day");
  }
  return days;
};

const toApiStatus = (
  status: LaborAttendanceStatus,
): Exclude<AttendanceApiStatus, "pending"> | null => {
  switch (status) {
    case "PRESENT":
      return "present";
    case "ABSENT":
      return "absent";
    case "HALF_DAY":
      return "halfday";
    default:
      return null;
  }
};

const fromApiStatus = (
  status: AttendanceApiStatus | null | undefined,
): LaborAttendanceStatus => {
  switch (status) {
    case "present":
      return "PRESENT";
    case "absent":
      return "ABSENT";
    case "halfday":
      return "HALF_DAY";
    default:
      return "--";
  }
};

const STATUSES_NEEDING_DETAILS: LaborAttendanceStatus[] = [
  "PRESENT",
  "HALF_DAY",
];

const DEFAULT_CHECK_IN_TIME = "09:00";
const DEFAULT_CHECK_OUT_TIME = "18:00";
const HALF_DAY_CHECK_OUT_TIME = "13:00";

const getDefaultTimesForStatus = (
  status: LaborAttendanceStatus,
): { checkInTime: string; checkOutTime: string } => {
  if (status === "HALF_DAY") {
    return {
      checkInTime: DEFAULT_CHECK_IN_TIME,
      checkOutTime: HALF_DAY_CHECK_OUT_TIME,
    };
  }
  return {
    checkInTime: DEFAULT_CHECK_IN_TIME,
    checkOutTime: DEFAULT_CHECK_OUT_TIME,
  };
};

interface ICellAttendanceDetails {
  status: LaborAttendanceStatus;
  checkInTime?: string;
  checkOutTime?: string;
  paidForThisDays: boolean;
}

interface IEditingCell {
  rowId: string;
  fullDate: string;
  status: LaborAttendanceStatus;
}

const cellKey = (rowId: string, fullDate: string) => `${rowId}__${fullDate}`;

const BulkAttendance: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
  });
  const [rows, setRows] = useState<ILaborBulkAttendanceRow[]>([]);

  const [savingCells, setSavingCells] = useState<Record<string, boolean>>({});

  const [attendanceIdMap, setAttendanceIdMap] = useState<
    Record<string, Record<string, string>>
  >({});
  const [workerCodeMap, setWorkerCodeMap] = useState<Record<string, string>>(
    {},
  );

  // ─── Confirm-details dialog state (Present / Half Day) ────────────────
  const [editingCell, setEditingCell] = useState<IEditingCell | null>(null);
  const [dialogCheckInTime, setDialogCheckInTime] = useState(
    DEFAULT_CHECK_IN_TIME,
  );
  const [dialogCheckOutTime, setDialogCheckOutTime] = useState(
    DEFAULT_CHECK_OUT_TIME,
  );
  const [dialogPaidForThisDays, setDialogPaidForThisDays] = useState(true);

  // ─── Global toggle: icon-only cells vs icon+label cells ────────────────
  const [isExpandedView, setIsExpandedView] = useState(false);

  const dayColumnsList = useMemo(
    () => getDayColumns(filter.startDate, filter.endDate),
    [filter.startDate, filter.endDate],
  );

  const fetchBulkAttendance = async () => {
    setIsLoading(true);
    try {
      const response = await AttendanceService.getAttendanceMatrix({
        startDate: filter.startDate,
        endDate: filter.endDate,
        search,
        pageSize: 100, // bump / paginate if you expect more workers than this
      });

      const matrixRows = response.data.rows;
      const nextAttendanceIdMap: Record<string, Record<string, string>> = {};
      const nextWorkerCodeMap: Record<string, string> = {};

      const mappedRows: ILaborBulkAttendanceRow[] = matrixRows.map((row) => {
        const workerId = row.worker.id;
        nextWorkerCodeMap[workerId] = row.worker.workerId;
        const attendanceMap: Record<string, LaborAttendanceStatus> = {};
        const attendanceByDate = new Map(
          row.attendance.map((day) => [day.date, day]),
        );

        dayColumnsList.forEach((d) => {
          const day = attendanceByDate.get(d.fullDate);
          // Key by fullDate (unique across months) instead of dayStr (which repeats: "01".."31" every month)
          // Sundays always show "--" in the UI regardless of what the API says
          attendanceMap[d.fullDate] = d.isSunday
            ? "--"
            : fromApiStatus(day?.status);

          if (day?.attendanceId) {
            nextAttendanceIdMap[workerId] = {
              ...nextAttendanceIdMap[workerId],
              [d.fullDate]: day.attendanceId,
            };
          }
        });

        return {
          id: workerId,
          laborName: row.worker.fullName,
          site: row.worker.Site?.siteName ?? "—",
          attendanceMap,
        };
      });

      setRows(mappedRows);
      setAttendanceIdMap(nextAttendanceIdMap);
      setWorkerCodeMap(nextWorkerCodeMap);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBulkAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.startDate, filter.endDate, search]);

  const setCellStatusInTable = (
    rowId: string,
    fullDate: string,
    status: LaborAttendanceStatus,
  ) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              attendanceMap: { ...r.attendanceMap, [fullDate]: status },
            }
          : r,
      ),
    );
  };

  const saveCell = async (
    rowId: string,
    fullDate: string,
    details: ICellAttendanceDetails,
    previousStatus: LaborAttendanceStatus,
  ) => {
    const key = cellKey(rowId, fullDate);

    setCellStatusInTable(rowId, fullDate, details.status);
    setSavingCells((prev) => ({ ...prev, [key]: true }));

    try {
      const apiStatus = toApiStatus(details.status);
      const existingAttendanceId = attendanceIdMap[rowId]?.[fullDate];

      if (!apiStatus) {
        if (existingAttendanceId) {
          await AttendanceService.deleteAttendance(existingAttendanceId);
          setAttendanceIdMap((prev) => {
            const rowMap = { ...prev[rowId] };
            delete rowMap[fullDate];
            return { ...prev, [rowId]: rowMap };
          });
        }
        return;
      }

      const payload = {
        date: fullDate,
        workerId: workerCodeMap[rowId] ?? rowId,
        checkInTime: details.checkInTime,
        checkOutTime: details.checkOutTime,
        status: apiStatus,
        paidForThisDays: details.paidForThisDays,
      };

      if (existingAttendanceId) {
        await AttendanceService.updateAttendance(existingAttendanceId, payload);
      } else {
        const createResponse =
          await AttendanceService.createAttendance(payload);
        const newAttendanceId = createResponse?.data?.id;
        if (newAttendanceId) {
          setAttendanceIdMap((prev) => ({
            ...prev,
            [rowId]: { ...prev[rowId], [fullDate]: newAttendanceId },
          }));
        }
      }
    } catch (error) {
      setCellStatusInTable(rowId, fullDate, previousStatus);
      ThrowError(error);
    } finally {
      setSavingCells((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleStatusSelect = (
    rowId: string,
    fullDate: string,
    status: LaborAttendanceStatus,
  ) => {
    const row = rows.find((r) => r.id === rowId);
    const previousStatus = row?.attendanceMap[fullDate] ?? "--";

    if (status === "--") {
      saveCell(
        rowId,
        fullDate,
        { status: "--", paidForThisDays: false },
        previousStatus,
      );
      return;
    }

    if (status === "ABSENT") {
      saveCell(
        rowId,
        fullDate,
        {
          status: "ABSENT",
          checkInTime: undefined,
          checkOutTime: undefined,
          paidForThisDays: false,
        },
        previousStatus,
      );
      return;
    }

    if (STATUSES_NEEDING_DETAILS.includes(status)) {
      const defaults = getDefaultTimesForStatus(status);
      setEditingCell({ rowId, fullDate, status });
      setDialogCheckInTime(defaults.checkInTime);
      setDialogCheckOutTime(defaults.checkOutTime);
      setDialogPaidForThisDays(true);
    }
  };

  const handleConfirmCellDetails = () => {
    if (!editingCell) return;

    const row = rows.find((r) => r.id === editingCell.rowId);
    const previousStatus = row?.attendanceMap[editingCell.fullDate] ?? "--";

    saveCell(
      editingCell.rowId,
      editingCell.fullDate,
      {
        status: editingCell.status,
        checkInTime: dialogCheckInTime,
        checkOutTime: dialogCheckOutTime,
        paidForThisDays: dialogPaidForThisDays,
      },
      previousStatus,
    );

    setEditingCell(null);
  };

  const todayStr = moment().format("YYYY-MM-DD");

  const columns: TableColumn<ILaborBulkAttendanceRow>[] = useMemo(() => {
    const fixedCols: TableColumn<ILaborBulkAttendanceRow>[] = [
      {
        key: "sNo",
        header: "#",
        headerClassName:
          "text-center border-r border-slate-200 bg-slate-50 font-semibold text-slate-500",
        className: "w-10 text-center border-r border-slate-200/80",
        render: (_item, index) => (
          <span className="text-xs font-semibold text-slate-400">
            {index + 1}
          </span>
        ),
      },
      {
        key: "laborName",
        header: "Labor",
        headerClassName:
          "border-r border-slate-200 bg-slate-50 font-semibold text-slate-500",
        className: "min-w-[210px] border-r border-slate-200/80 bg-white",
        render: (item) => (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
              {item.laborName.charAt(0).toUpperCase()}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-slate-800 truncate">
                {item.laborName}
              </span>
              <span className="text-[11px] text-slate-400 truncate">
                {item.site}
              </span>
            </div>
          </div>
        ),
      },
    ];

    // ─── Day columns: keyed by fullDate (unique), displaying dayStr/dayName ─
    const dayCols: TableColumn<ILaborBulkAttendanceRow>[] = dayColumnsList.map(
      (dayObj) => {
        const isToday = dayObj.fullDate === todayStr;
        const isFuture = moment(dayObj.fullDate).isAfter(moment(), "day");
        return {
          key: dayObj.fullDate, // unique across months now
          header: (
            <div
              className={`flex flex-col items-center py-2 rounded-lg ${isToday ? "bg-primary/10" : ""}`}
            >
              <span
                className={`text-[11px] uppercase font-semibold tracking-wide ${
                  dayObj.isSunday ? "text-rose-500" : "text-slate-400"
                }`}
              >
                {dayObj.dayName}
              </span>
              <span
                className={`text-sm font-bold mt-0.5 ${
                  isToday
                    ? "text-primary"
                    : dayObj.isSunday
                      ? "text-rose-500"
                      : "text-slate-700"
                }`}
              >
                {dayObj.dayStr}
              </span>
            </div>
          ),
          headerClassName: `text-center border-r border-slate-200 min-w-[110px] ${
            dayObj.isSunday ? "bg-rose-50/70" : "bg-slate-50"
          }`,
          className: `text-center min-w-[110px] border-r border-slate-200/70 p-2 ${
            dayObj.isSunday ? "bg-rose-50/30" : isToday ? "bg-primary/5" : ""
          }`,
          render: (item) => {
            const status: LaborAttendanceStatus =
              item.attendanceMap[dayObj.fullDate] || "--"; // read by fullDate
            const label =
              LABOR_STATUS_OPTIONS.find((o) => o.value === status)?.label ||
              status;
            const StatusIcon = LABOR_STATUS_ICON_MAP[status];
            const isSaving = !!savingCells[cellKey(item.id, dayObj.fullDate)];
            const isDisabled = isFuture || dayObj.isSunday || isSaving;

            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={isDisabled}>
                  <button
                    disabled={isDisabled}
                    title={
                      isFuture
                        ? "This date hasn't happened yet"
                        : dayObj.isSunday
                          ? "Sundays are not marked"
                          : isSaving
                            ? "Saving…"
                            : label // icon-only button — tooltip carries the status text
                    }
                    className={`h-9 mx-auto flex items-center justify-center gap-1.5 rounded-full border-2 overflow-hidden transition-[width,padding] duration-200 ${
                      isExpandedView ? "w-auto px-3" : "w-9"
                    } ${
                      isDisabled
                        ? "bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed"
                        : `${LABOR_STATUS_STYLE_MAP[status]} cursor-pointer shadow-sm`
                    }`}
                  >
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                    ) : isFuture || dayObj.isSunday ? (
                      <MinusCircle className="h-4 w-4 shrink-0" />
                    ) : (
                      <>
                        <StatusIcon className="h-4 w-4 shrink-0" />
                        {isExpandedView && (
                          <span className="text-xs font-bold whitespace-nowrap">
                            {label}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="min-w-[140px]">
                  {LABOR_STATUS_OPTIONS.map((opt) => {
                    const OptIcon = LABOR_STATUS_ICON_MAP[opt.value];
                    return (
                      <DropdownMenuItem
                        key={opt.value}
                        onClick={() =>
                          handleStatusSelect(
                            item.id,
                            dayObj.fullDate,
                            opt.value,
                          )
                        }
                        className="cursor-pointer text-xs font-semibold flex items-center gap-2"
                      >
                        <OptIcon className="h-3.5 w-3.5" />
                        {opt.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          },
        };
      },
    );

    return [...fixedCols, ...dayCols];
  }, [dayColumnsList, todayStr, savingCells, rows, isExpandedView]);

  return (
    <>
      <div className="space-y-6 pb-10">
        <MainHeading
          title="Manage Bulk Attendance"
          subtitle={`${moment(filter.startDate).format("D MMM")} – ${moment(
            filter.endDate,
          ).format("D MMM YYYY")} · ${rows.length} ${
            rows.length === 1 ? "worker" : "workers"
          }`}
          // isBackButtonVisible
        />

        <Card className="p-6 space-y-5 shadow-sm border-slate-200/80 rounded-2xl bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <LaborAttendanceLegend />
            <div className="flex items-center gap-4 shrink-0">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <CalendarDays className="h-3.5 w-3.5" />
                Future dates are locked until they arrive
              </div>
              <button
                type="button"
                onClick={() => setIsExpandedView((prev) => !prev)}
                title={isExpandedView ? "Collapse to icons" : "Expand to show labels"}
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 transition-colors"
              >
                {isExpandedView ? (
                  <Shrink className="h-3.5 w-3.5" />
                ) : (
                  <Expand className="h-3.5 w-3.5" />
                )}
                {isExpandedView ? "Collapse" : "Expand"}
              </button>
            </div>
          </div>

          <CommonFilter
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search labor name..."
            startDate={filter.startDate}
            endDate={filter.endDate}
            onDateRangeChange={(start, end) =>
              setFilter({
                startDate:
                  start || moment().startOf("month").format("YYYY-MM-DD"),
                endDate: end || moment().endOf("month").format("YYYY-MM-DD"),
              })
            }
          />

          <div className="w-full overflow-x-auto border border-slate-200 rounded-xl shadow-sm bg-white">
            <CommonTable
              columns={columns}
              data={rows}
              loading={isLoading}
              tableClassName="border-collapse divide-x divide-slate-200"
              stickyColumns={2}
            />
          </div>
        </Card>
      </div>

      <Dialog
        open={!!editingCell}
        onOpenChange={(open) => {
          if (!open) setEditingCell(null);
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {editingCell?.status === "HALF_DAY"
                ? "Confirm half-day details"
                : "Confirm attendance details"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="checkInTime">Check-in time</Label>
              <Input
                id="checkInTime"
                type="time"
                value={dialogCheckInTime}
                onChange={(e) => setDialogCheckInTime(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="checkOutTime">Check-out time</Label>
              <Input
                id="checkOutTime"
                type="time"
                value={dialogCheckOutTime}
                onChange={(e) => setDialogCheckOutTime(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="paidForThisDays"
                type="checkbox"
                checked={dialogPaidForThisDays}
                onChange={(e) => setDialogPaidForThisDays(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
              />
              <Label htmlFor="paidForThisDays" className="cursor-pointer">
                Paid for this day
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingCell(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmCellDetails}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkAttendance;